import React, { useState, useEffect } from 'react';
import { Card, Button, Typography, Space, Tag, Row, Col, Divider, Form, Input, message, Alert, Statistic, Timeline, Badge, Spin, Breadcrumb, Tabs } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, CalendarOutlined, UserOutlined, DollarOutlined, FileTextOutlined, ExclamationCircleOutlined, ArrowLeftOutlined, CreditCardOutlined, CalculatorOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import { getStatusColor, formatCurrency } from '../../../utils/ui';
import dayjs from 'dayjs';
import QuotePaymentManager from '../../Quotes/QuotePaymentManager';
import QuotePaymentViewer from '../../Quotes/QuotePaymentViewer';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

function QuoteDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [quote, setQuote] = useState(null);
    const [loading, setLoading] = useState(false);
    const [responding, setResponding] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [form] = Form.useForm();

    const normalizeQuote = (raw) => {
        if (!raw || typeof raw !== 'object') return null;
        
        // Handle both direct quote data and nested data structure
        const quote = raw?.data || raw;
        
        return {
            id: quote.id,
            quote_number: quote.quote_number,
            status: quote.status,
            event_details: quote.event_details || {},
            service_requirements: quote.service_requirements || {},
            packages: quote.packages || [],
            special_instructions: quote.special_instructions || quote.instructions?.special_instructions,
            customer_notes: quote.customer_notes || quote.instructions?.customer_notes,
            vendor_notes: quote.vendor_notes || quote.instructions?.vendor_notes,
            subtotal: quote.subtotal || quote.pricing?.subtotal || 0,
            service_cost: quote.service_cost || quote.pricing?.service_cost || 0,
            tax_amount: quote.tax_amount || quote.pricing?.tax_amount || 0,
            total_amount: quote.total_amount || quote.pricing?.total_amount || 0,
            discount_percentage: quote.discount_percentage || quote.pricing?.discount_percentage || 0,
            discount_amount: quote.discount_amount || quote.pricing?.discount_amount || 0,
            base_price_per_person: quote.base_price_per_person || quote.pricing?.base_price_per_person || 0,
            valid_until: quote.valid_until || quote.timing?.valid_until,
            created_at: quote.created_at || quote.timing?.created_at,
            responded_at: quote.responded_at || quote.timing?.responded_at,
            customer: quote.customer || quote.relationships?.customer,
            vendor: quote.vendor || quote.relationships?.vendor,
            is_expired: quote.is_expired || quote.metadata?.is_expired || false,
            can_be_responded: quote.can_be_responded || quote.metadata?.can_be_responded || false,
            days_until_expiry: quote.days_until_expiry || quote.metadata?.days_until_expiry,
            is_overdue: quote.is_overdue || quote.metadata?.is_overdue || false
        };
    };

    useEffect(() => {
        if (id) {
            fetchQuote();
        }
    }, [id]);

    const fetchQuote = async () => {
        try {
            setLoading(true);
            setLoadError('');
            
            const response = await api.getQuote(id);
            const normalizedQuote = normalizeQuote(response.data);
            
            if (normalizedQuote) {
                setQuote(normalizedQuote);
            } else {
                setLoadError('Quote not found or invalid data format');
            }
        } catch (error) {
            console.error('Quote fetch error:', error);
            setLoadError('Failed to load quote details');
        } finally {
            setLoading(false);
        }
    };

    const handleAccept = async (values) => {
        // Optimistic UI: mark accepted and navigate
        setResponding(true);
        const prev = quote;
        setQuote(prev ? { ...prev, status: 'accepted', responded_at: new Date().toISOString() } : prev);
        message.loading({ content: 'Accepting quote...', key: `accept-${id}` });

        try {
            const response = await api.acceptQuote(id, values.notes || '');
            message.success({ content: 'Quote accepted successfully!', key: `accept-${id}`, duration: 2 });
            navigate('/quotes');
        } catch (error) {
            // Revert on failure
            setQuote(prev);
            console.error('Accept quote error:', error);
            message.error({ content: 'Failed to accept quote', key: `accept-${id}` });
        } finally {
            setResponding(false);
        }
    };

    const handleReject = async (values) => {
        try {
            setResponding(true);
            const response = await api.rejectQuote(id, values.notes || '');
            
            if (response.success) {
                message.success('Quote rejected');
                navigate('/quotes');
            } else {
                message.error(response.message || 'Failed to reject quote');
            }
        } catch (error) {
            console.error('Reject quote error:', error);
            message.error('Failed to reject quote');
        } finally {
            setResponding(false);
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'pending': return <ClockCircleOutlined />;
            case 'quoted': return <FileTextOutlined />;
            case 'accepted': return <CheckCircleOutlined />;
            case 'rejected': return <CloseCircleOutlined />;
            case 'expired': return <ExclamationCircleOutlined />;
            default: return <ClockCircleOutlined />;
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <Spin size="large" />
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="max-w-4xl mx-auto p-4">
                <Alert
                    message="Error"
                    description={loadError}
                    type="error"
                    showIcon
                    action={
                        <Button onClick={() => navigate('/quotes')}>
                            Back to Quotes
                        </Button>
                    }
                />
            </div>
        );
    }

    if (!quote) {
        return (
            <div className="max-w-4xl mx-auto p-4">
                <Alert
                    message="Quote Not Found"
                    description="The requested quote could not be found."
                    type="warning"
                    showIcon
                    action={
                        <Button onClick={() => navigate('/quotes')}>
                            Back to Quotes
                        </Button>
                    }
                />
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto p-4 space-y-6">
            {/* Breadcrumb */}
            <Breadcrumb>
                <Breadcrumb.Item>
                    <Button 
                        type="link" 
                        icon={<ArrowLeftOutlined />} 
                        onClick={() => navigate('/quotes')}
                        className="p-0"
                    >
                        Quotes
                    </Button>
                </Breadcrumb.Item>
                <Breadcrumb.Item>{quote.quote_number}</Breadcrumb.Item>
            </Breadcrumb>

            {/* Header */}
            <Card>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <Title level={2} className="!mb-2">
                            {quote.quote_number || `Quote #${quote.id}`}
                        </Title>
                        <div className="flex items-center gap-2">
                            <Tag 
                                color={getStatusColor(quote.status)} 
                                icon={getStatusIcon(quote.status)}
                                className="text-sm"
                            >
                                {quote.status.toUpperCase()}
                            </Tag>
                            {quote.is_expired && (
                                <Tag color="red">EXPIRED</Tag>
                            )}
                        </div>
                    </div>
                    
                    <div className="flex gap-2">
                        <Button 
                            icon={<ArrowLeftOutlined />} 
                            onClick={() => navigate('/quotes')}
                        >
                            Back
                        </Button>
                        {(quote.status === 'pending' || quote.status === 'quoted') && quote.can_be_responded && (
                            <Space>
                                <Form form={form} onFinish={handleAccept}>
                                    <Form.Item name="notes" className="mb-0">
                                        <TextArea 
                                            placeholder="Add notes (optional)" 
                                            rows={2}
                                            className="mb-2"
                                        />
                                    </Form.Item>
                                    <Button 
                                        type="primary" 
                                        icon={<CheckCircleOutlined />}
                                        loading={responding}
                                        htmlType="submit"
                                    >
                                        Accept Quote
                                    </Button>
                                </Form>
                                <Form form={form} onFinish={handleReject}>
                                    <Button 
                                        danger 
                                        icon={<CloseCircleOutlined />}
                                        loading={responding}
                                        htmlType="submit"
                                    >
                                        Reject Quote
                                    </Button>
                                </Form>
                            </Space>
                        )}
                    </div>
                </div>
            </Card>

            {/* Quote Details */}
            <Row gutter={[16, 16]}>
                {/* Event Details */}
                <Col xs={24} lg={12}>
                    <Card title="Event Details" size="small">
                        <div className="space-y-3">
                            <div className="flex items-center gap-2">
                                <CalendarOutlined className="text-blue-500" />
                                <div>
                                    <Text strong>Event Date</Text>
                                    <div className="text-sm text-gray-600">
                                        {quote.event_details?.date ? 
                                            dayjs(quote.event_details.date).format('MMMM DD, YYYY') : 
                                            quote.event_date ? 
                                            dayjs(quote.event_date).format('MMMM DD, YYYY') : 
                                            'Not specified'
                                        }
                                    </div>
                                </div>
                            </div>
                            
                            <div className="flex items-center gap-2">
                                <UserOutlined className="text-green-500" />
                                <div>
                                    <Text strong>Guest Count</Text>
                                    <div className="text-sm text-gray-600">
                                        {quote.event_details?.guest_count || quote.guest_count || 'Not specified'}
                                    </div>
                                </div>
                            </div>
                            
                            <div className="flex items-center gap-2">
                                <FileTextOutlined className="text-purple-500" />
                                <div>
                                    <Text strong>Event Type</Text>
                                    <div className="text-sm text-gray-600">
                                        {quote.event_details?.event_type || quote.event_details?.occasion || 'Not specified'}
                                    </div>
                                </div>
                            </div>
                            
                            {quote.event_details?.venue && (
                                <div className="flex items-center gap-2">
                                    <CalendarOutlined className="text-orange-500" />
                                    <div>
                                        <Text strong>Venue</Text>
                                        <div className="text-sm text-gray-600">
                                            {quote.event_details.venue}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </Card>
                </Col>

                {/* Pricing */}
                <Col xs={24} lg={12}>
                    <Card title="Pricing Details" size="small">
                        <div className="space-y-3">
                            {quote.base_price_per_person > 0 && (
                                <div className="flex justify-between">
                                    <Text>Base Price Per Person:</Text>
                                    <Text>{formatCurrency(quote.base_price_per_person)}</Text>
                                </div>
                            )}
                            <div className="flex justify-between">
                                <Text>Subtotal:</Text>
                                <Text>{formatCurrency(quote.subtotal)}</Text>
                            </div>
                            <div className="flex justify-between">
                                <Text>Service Cost:</Text>
                                <Text>{formatCurrency(quote.service_cost)}</Text>
                            </div>
                            {quote.discount_percentage > 0 && (
                                <div className="flex justify-between">
                                    <Text>Discount ({quote.discount_percentage?.toFixed(1)}%):</Text>
                                    <Text className="text-red-600">-{formatCurrency(quote.discount_amount || 0)}</Text>
                                </div>
                            )}
                            <div className="flex justify-between">
                                <Text>Tax (18%):</Text>
                                <Text>{formatCurrency(quote.tax_amount)}</Text>
                            </div>
                            <Divider className="my-2" />
                            <div className="flex justify-between">
                                <Text strong className="text-lg">Total Amount:</Text>
                                <Text strong className="text-lg text-orange-600">
                                    {formatCurrency(quote.total_amount)}
                                </Text>
                            </div>
                            
                            {quote.valid_until && (
                                <div className="mt-4 p-3 bg-gray-50 rounded">
                                    <Text strong>Valid Until:</Text>
                                    <div className="text-sm text-gray-600">
                                        {dayjs(quote.valid_until).format('MMMM DD, YYYY')}
                                    </div>
                                </div>
                            )}
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Vendor Information */}
            <Card title="Vendor Information" size="small">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                        <UserOutlined className="text-blue-600 text-xl" />
                    </div>
                    <div>
                        <Text strong className="text-lg">
                            {quote.vendor?.name || quote.vendor?.business_name || 'Unknown Vendor'}
                        </Text>
                        <div className="text-sm text-gray-600">
                            {quote.vendor?.email || 'No email provided'}
                        </div>
                    </div>
                </div>
            </Card>

            {/* Instructions & Notes */}
            {(quote.special_instructions || quote.vendor_notes || quote.customer_notes) && (
                <Card title="Instructions & Notes" size="small">
                    <div className="space-y-4">
                        {quote.special_instructions && (
                            <div>
                                <Text strong>Special Instructions:</Text>
                                <div className="text-sm text-gray-600 mt-1">
                                    {quote.special_instructions}
                                </div>
                            </div>
                        )}
                        
                        {quote.vendor_notes && (
                            <div>
                                <Text strong>Vendor Notes:</Text>
                                <div className="text-sm text-gray-600 mt-1">
                                    {quote.vendor_notes}
                                </div>
                            </div>
                        )}
                        
                        {quote.customer_notes && (
                            <div>
                                <Text strong>Customer Notes:</Text>
                                <div className="text-sm text-gray-600 mt-1">
                                    {quote.customer_notes}
                                </div>
                            </div>
                        )}
                    </div>
                </Card>
            )}

            {/* Packages */}
            {quote.packages && quote.packages.length > 0 && (
                <Card title="Menu Items & Packages" size="small">
                    <div className="space-y-3">
                        {quote.packages.map((pkg, index) => (
                            <div key={index} className="p-3 border rounded">
                                <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                        <Text strong>{pkg.name}</Text>
                                        <div className="text-sm text-gray-600 mt-1">
                                            {pkg.description}
                                        </div>
                                        {pkg.quantity && (
                                            <div className="text-sm text-blue-600 mt-1">
                                                Quantity: {pkg.quantity}
                                            </div>
                                        )}
                                    </div>
                                    <div className="text-right">
                                        <div className="text-sm text-orange-600">
                                            {formatCurrency(pkg.price)}
                                        </div>
                                        {pkg.quantity && (
                                            <div className="text-xs text-gray-500">
                                                Total: {formatCurrency(pkg.price * pkg.quantity)}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            )}

            {/* Auto-calculated quantities info */}
            {quote.service_requirements?.auto_calculated && (
                <Card title="Calculation Details" size="small">
                    <Alert
                        message="Auto-calculated Quantities"
                        description="Quantities have been automatically calculated based on the number of guests to ensure adequate portions."
                        type="info"
                        showIcon
                        icon={<CalculatorOutlined />}
                    />
                </Card>
            )}

            {/* Payment Management Tabs - Only for Vendors */}
            {quote.status === 'accepted' && quote.vendor_id === JSON.parse(localStorage.getItem('user') || '{}').id && (
                <Card title="Payment Management" size="small">
                    <Tabs
                        defaultActiveKey="payments"
                        items={[
                            {
                                key: 'payments',
                                label: (
                                    <span>
                                        <CreditCardOutlined />
                                        Payments
                                    </span>
                                ),
                                children: (
                                    <QuotePaymentManager 
                                        quote={quote} 
                                        onPaymentSuccess={() => {
                                            message.success('Payment processed successfully!');
                                            fetchQuote();
                                        }}
                                    />
                                )
                            }
                        ]}
                    />
                </Card>
            )}

            {/* Payment Viewer for Customers */}
            {quote.status === 'accepted' && quote.customer_id === JSON.parse(localStorage.getItem('user') || '{}').id && (
                <Card title="Payment Options" size="small">
                    <QuotePaymentViewer 
                        quote={quote} 
                        onPaymentSuccess={() => {
                            message.success('Payment processed successfully!');
                            fetchQuote();
                        }}
                    />
                </Card>
            )}
        </div>
    );
}

export default QuoteDetail;
